"use client";

import { Instagram } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useTranslations } from "next-intl";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import instaImage1 from "@/app/assets/illustration/insta_1.jpg";
import instaImage2 from "@/app/assets/illustration/insta_2.jpg";
import instaImage3 from "@/app/assets/illustration/insta_3.jpg";
import instaLogo from "@/app/assets/insta_logo.jpg";

interface InstagramSectionProps {
    className?: string;
}

export const InstagramSection = ({ className = "" }: InstagramSectionProps) => {
    const t = useTranslations("Instagram");

    const posts = [
        {
            alt: "Want better sleep for the whole family? Fill that tiny emotional cup first!",
            id: 1,
            image: instaImage1,
            link: "https://www.instagram.com/p/DHBcV3rNYt2/",
            text: "Want better sleep for the whole family? Fill that tiny emotional cup first! Think of your child's emotions like a sippy...",
        },
        {
            alt: "You are a good mom",
            id: 2,
            image: instaImage2,
            link: "https://www.instagram.com/p/DH56tBKNEA9/",
            text: "Never doubt it — you are the best mom for your child.",
        },
        {
            alt: "Why choose us, you ask?",
            id: 3,
            image: instaImage3,
            link: "https://www.instagram.com/p/DHdm-jxNGdh/",
            text: "Why choose us, you ask? Why spend hundreds on a sleep consultant...",
        },
    ];

    return (
        <section className={`${className}`}>
            <div className="container mx-auto px-4">
                <div className="text-center mb-16 max-w-3xl mx-auto">
                    <h2 className="text-4xl font-bold mb-6 whitespace-pre-line">{t("header.title")}</h2>
                    <p className="text-black text-2xl font-normal mb-8 whitespace-pre-line">
                        {t("header.description")}
                    </p>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                        {t.raw("header.benefits").map((benefit: string, index: number) => (
                            <li className="flex items-center gap-2" key={index}>
                                <div className="text-2xl text-primary-100">✓</div>
                                <span className="text-black text-lg font-normal">{benefit}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="hidden md:grid grid-cols-3 gap-8">
                    {posts.map((post) => (
                        <Link
                            className="block overflow-hidden bg-white rounded-lg hover:shadow-lg transition-shadow duration-300 border border-primary-300/60"
                            href={post.link}
                            key={post.id}
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            <div className="space-y-3">
                                <div className="aspect-square relative rounded-lg overflow-hidden">
                                    <Image alt={post.alt} className="object-cover" fill src={post.image} />
                                </div>
                                <div className="flex items-center justify-between px-3 pt-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-full overflow-hidden relative flex-shrink-0">
                                            <Image alt="Easy Sleep Lab" className="object-cover" fill src={instaLogo} />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-medium text-sm">Easy Sleep Lab</p>
                                            <p className="text-sm text-gray-500">@easy_sleep_lab</p>
                                        </div>
                                    </div>
                                    <Instagram className="w-6 h-6" style={{ color: "#E4405F" }} />
                                </div>
                                <p className="text-sm px-3 pb-3">{post.text}</p>
                            </div>
                        </Link>
                    ))}
                </div>

                <div className="md:hidden">
                    <Swiper
                        centeredSlides
                        className="!pb-12"
                        modules={[Pagination]}
                        pagination={{ clickable: true }}
                        slidesPerView={1.2}
                        spaceBetween={16}
                        style={
                            {
                                "--swiper-pagination-bullet-inactive-color": "#e8d7d1",
                                "--swiper-pagination-bullet-inactive-opacity": "1",
                                "--swiper-pagination-bullet-size": "12px",
                                "--swiper-pagination-color": "var(--primary)",
                            } as React.CSSProperties
                        }
                    >
                        {posts.map((post) => (
                            <SwiperSlide key={post.id}>
                                <Link
                                    className="block overflow-hidden bg-white rounded-lg hover:shadow-lg transition-shadow duration-300 border border-primary-300/60"
                                    href={post.link}
                                    rel="noopener noreferrer"
                                    target="_blank"
                                >
                                    <div className="space-y-3">
                                        <div className="aspect-square relative rounded-lg overflow-hidden">
                                            <Image alt={post.alt} className="object-cover" fill src={post.image} />
                                        </div>
                                        <div className="flex items-center justify-between px-3 pt-3">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full overflow-hidden relative flex-shrink-0">
                                                    <Image
                                                        alt="Easy Sleep Lab"
                                                        className="object-cover"
                                                        fill
                                                        src={instaLogo}
                                                    />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-medium text-sm">Easy Sleep Lab</p>
                                                    <p className="text-sm text-gray-500">@easy_sleep_lab</p>
                                                </div>
                                            </div>
                                            <Instagram className="w-6 h-6" style={{ color: "#E4405F" }} />
                                        </div>
                                        <p className="text-sm px-3 pb-3">{post.text}</p>
                                    </div>
                                </Link>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </div>
        </section>
    );
};
